import { forwardRef, type ReactElement, type Ref } from 'react';

import {
  TableBody as RACTableBody,
  type TableBodyProps as RACTableBodyProps,
} from 'react-aria-components/Table';

import styles from './table_view.module.css';

export type TableBodyProps<T> = RACTableBodyProps<T> & {
  className?: string;
  /** Message to show in empty state. If not provided, uses default empty state. */
  emptyStateMessage?: {
    title: string;
    description?: string;
  };
};

/**
 * TableBody - Container for table rows.
 */
// biome-ignore lint/plugin: forwardRef cannot carry a type parameter in React; the result is re-typed to a generic signature so callers keep row-type inference.
export const TableBody = forwardRef<
  HTMLTableSectionElement,
  TableBodyProps<any>
>(({ className, emptyStateMessage, renderEmptyState, ...props }, ref) => {
  // Create default empty state renderer if emptyStateMessage is provided
  const defaultEmptyStateRenderer = emptyStateMessage
    ? () => (
        <div className={styles.empty_state}>
          <h3 className={styles.empty_state_title}>
            {emptyStateMessage.title}
          </h3>
          {emptyStateMessage.description && (
            <p className={styles.empty_state_description}>
              {emptyStateMessage.description}
            </p>
          )}
        </div>
      )
    : undefined;

  return (
    <RACTableBody
      ref={ref}
      className={className}
      renderEmptyState={renderEmptyState || defaultEmptyStateRenderer}
      {...props}
    >
      {props.children}
    </RACTableBody>
  );
}) as <T>(
  props: TableBodyProps<T> & { ref?: Ref<HTMLTableSectionElement> },
) => ReactElement;
